import React from "react";
import LegalPage from "./legal/LegalPage";

export default function Guidelines() {
  return (
    <LegalPage
      kicker="Community"
      title={<>Our Shared Commitments<br /><em>&amp; Community Guidelines.</em></>}
      updated="February 2026"
      testId="guidelines-page"
    >
      <p>
        <strong>LocalizeHER</strong> is a community for women business owners who are building
        something meaningful in their cities and communities.
      </p>
      <p>
        Whether you&rsquo;re a solo entrepreneur, a service provider, a storefront owner, or
        leading a growing team, this is a place to learn, collaborate, ask questions, share wins,
        and support one another.
      </p>
      <p>
        Our goal is simple: create a safe, inclusive, judgment-free environment where women can
        get real business support without the pressure, politics, or performative networking
        often found elsewhere.
      </p>
      <p>
        By joining LocalizeHER, you agree to follow these community guidelines.
      </p>

      <h2>1. Lead with Respect</h2>
      <p>Every member deserves to feel welcomed, valued, and heard.</p>
      <p>
        We encourage healthy discussions, diverse viewpoints, and constructive feedback. We do
        not tolerate:
      </p>
      <ul>
        <li>Harassment, bullying, or intimidation</li>
        <li>Hate speech or discriminatory language</li>
        <li>Personal attacks or public shaming</li>
        <li>Racist, sexist, homophobic, transphobic, or otherwise exclusionary behavior</li>
        <li>Threatening, abusive, or hostile communication</li>
      </ul>
      <p>
        LocalizeHER welcomes women from all backgrounds, identities, experiences, and industries.
        We are committed to maintaining an inclusive and respectful environment for everyone.
      </p>

      <h2>2. Collaboration Over Competition</h2>
      <p>We believe there is room for all of us to succeed.</p>
      <p>
        Members should approach conversations with generosity, integrity, and professionalism.
        While healthy business discussions are encouraged, intentionally undermining, attacking,
        or disparaging another member, their business, or their work is not permitted.
      </p>
      <p>Support one another. Share knowledge. Celebrate wins.</p>

      <h2>3. No Spam or Aggressive Self-Promotion</h2>
      <p>LocalizeHER is a community first.</p>
      <p>
        Members may share their businesses, products, services, events, and accomplishments in
        designated channels, discussions, or promotional opportunities approved by LocalizeHER.
      </p>
      <p>The following are prohibited:</p>
      <ul>
        <li>Unsolicited sales pitches</li>
        <li>Excessive promotional posting</li>
        <li>Mass direct messaging members for sales purposes</li>
        <li>Recruiting members into unrelated programs, groups, or communities</li>
        <li>Repeated posting of the same offer</li>
      </ul>
      <p>
        If your first interaction with another member is a sales pitch, you&rsquo;re doing it wrong.
      </p>
      <p>Build relationships before making asks.</p>

      <h2>4. Respect Member Privacy</h2>
      <p>What happens inside LocalizeHER stays inside LocalizeHER.</p>
      <p>Members may not:</p>
      <ul>
        <li>Share private conversations without permission</li>
        <li>Screenshot community discussions for public distribution</li>
        <li>Share confidential business information belonging to another member</li>
        <li>Use member contact information for unsolicited marketing</li>
      </ul>
      <p>Trust is the foundation of this community.</p>

      <h2>5. Keep Conversations Business-Focused</h2>
      <p>LocalizeHER exists to support women business owners.</p>
      <p>
        Topics related to marketing, branding, operations, leadership, finance, growth, wellness,
        entrepreneurship, and business ownership are encouraged.
      </p>
      <p>
        Content that is intentionally divisive, inflammatory, or unrelated to the purpose of the
        community may be removed at the discretion of LocalizeHER administrators.
      </p>

      <h2>6. Inclusive &amp; Judgment-Free Environment</h2>
      <p>LocalizeHER proudly welcomes:</p>
      <ul>
        <li>Women business owners</li>
        <li>LGBTQ+ entrepreneurs</li>
        <li>Trans-friendly business owners and allies</li>
        <li>Founders from all backgrounds, industries, and stages of business</li>
      </ul>
      <p>
        Discrimination, exclusion, or harassment based on race, ethnicity, religion, age,
        disability, gender identity, sexual orientation, nationality, or socioeconomic background
        will not be tolerated.
      </p>
      <p>Everyone deserves a seat at the table.</p>

      <h2>7. Event &amp; Workshop Attendance</h2>
      <p>
        If you register for an event, workshop, office hours session, mastermind, or member
        gathering, please be respectful of limited spots.
      </p>
      <p>
        If you cannot attend, we ask that you cancel as early as possible so another member can
        participate.
      </p>
      <p>Repeated no-shows may result in restrictions on future event registrations.</p>

      <h2>8. Intellectual Property &amp; Content Sharing</h2>
      <p>
        Members retain ownership of their own content, materials, intellectual property, and
        business ideas.
      </p>
      <p>
        Do not copy, distribute, sell, or repurpose another member&rsquo;s materials without their
        permission.
      </p>
      <p>Give credit where credit is due.</p>

      <h2>9. Follow Applicable Laws</h2>
      <p>
        Members are expected to comply with all applicable laws and regulations, including
        intellectual property, privacy, advertising, and business-related regulations.
      </p>
      <p>
        LocalizeHER is not responsible for agreements, transactions, partnerships, or business
        relationships formed between members.
      </p>

      <h2>10. Reporting Concerns</h2>
      <p>
        If you experience or witness behavior that violates these guidelines, please contact the
        LocalizeHER team.
      </p>
      <p>All concerns will be reviewed thoughtfully and confidentially.</p>
      <p>We are committed to protecting the integrity and safety of the community.</p>

      <h2>Membership Accountability</h2>
      <p>Our goal is always education before enforcement.</p>

      <h3>First Notice</h3>
      <p>
        A member may receive a reminder regarding community guidelines and be asked to correct
        the behavior.
      </p>

      <h3>Second Notice</h3>
      <p>If behavior continues, a formal warning may be issued.</p>

      <h3>Membership Review</h3>
      <p>
        Repeated violations or serious misconduct may result in temporary suspension or permanent
        removal from LocalizeHER.
      </p>
      <p>Examples include:</p>
      <ul>
        <li>Harassment or discrimination</li>
        <li>Solicitation after being warned</li>
        <li>Sharing confidential information</li>
        <li>Repeated disruptive behavior</li>
        <li>Misrepresentation of identity</li>
        <li>Behavior that threatens the safety or integrity of the community</li>
      </ul>
      <p>
        Membership fees are non-refundable if a member is removed for violating community
        guidelines.
      </p>

      <h2>Membership Cancellation Policy</h2>
      <p>Members may cancel their membership at any time.</p>
      <p>
        Because membership provides immediate access to community resources, events, content, and
        benefits, membership fees are generally non-refundable.
      </p>
      <p>
        Event-specific refunds, if applicable, will be outlined on the registration page for each
        event.
      </p>

      <h2>Our Commitment</h2>
      <p>
        LocalizeHER was created because too many women are building businesses alone.
      </p>
      <p>
        This community exists to provide support, education, connection, and real-world business
        guidance in an environment that is welcoming, inclusive, and free from judgment.
      </p>
      <p>
        We&rsquo;re here to help women become category leaders in their communities&mdash;and we
        do that best when we support one another.
      </p>
      <p><strong>Welcome to LocalizeHER.</strong></p>
    </LegalPage>
  );
}
